'use client';

import { useSession } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';

import { GA_EVENTS, sendGAEvent } from '@/app/lib/analytics';
import { TERMS_OF_SERVICE_PDF_URL } from '@/app/lib/legal/agreements';
import { joinWaitlist } from '@/app/lib/waitlist';

// FAQ Item Component
interface FaqItemProps {
  question: string;
  answer: React.ReactNode;
}

function FaqItem({ question, answer }: FaqItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    // Send GA event when FAQ item is toggled
    sendGAEvent({
      ...(newIsOpen ? GA_EVENTS.FAQ.OPEN : GA_EVENTS.FAQ.CLOSE),
      label: question, // Track which question was interacted with
    });
  };

  return (
    <div
      className="border-b border-sourced-black w-full"
      style={{ borderBottomWidth: '1px' }}
    >
      <button
        className="flex justify-between w-full text-left text-sourced-black focus:outline-none"
        onClick={handleToggle}
        style={{
          paddingTop: '32px',
          paddingBottom: '32px',
          gap: '32px',
          width: '100%',
        }}
      >
        <span
          style={{
            fontWeight: 400,
            fontSize: '24px',
            lineHeight: '90%',
            letterSpacing: '-0.03em',
          }}
        >
          {question}
        </span>
        <div className="transition-duration-200 flex-shrink-0">
          <Image
            src={
              isOpen
                ? '/images/landing/faq-close.svg'
                : '/images/landing/faq-open.svg'
            }
            alt="Toggle"
            width={24}
            height={24}
            className="text-sourced-black w-6 h-6 min-w-[24px] min-h-[24px]"
          />
        </div>
      </button>
      {isOpen && (
        <div
          className="text-sourced-black pb-8 w-full md:max-w-[751px]"
          style={{
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '120%',
            letterSpacing: '0',
          }}
        >
          {answer}
        </div>
      )}
    </div>
  );
}

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isPlayingFullVideo, setIsPlayingFullVideo] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Auth/session
  const { data: session } = useSession();
  const isAuthenticated = !!session?.user;

  // Device-based dashboard target
  const dashboardHref = isMobile ? '/mobile' : '/web';

  // Refs
  const formRef = useRef<HTMLFormElement>(null);
  const fullVideoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  // Section refs for tracking visibility
  const heroSectionRef = useRef<HTMLElement>(null);
  const introSectionRef = useRef<HTMLDivElement>(null);
  const howItWorksSectionRef = useRef<HTMLElement>(null);
  const audiencesSectionRef = useRef<HTMLElement>(null);
  const teamSectionRef = useRef<HTMLElement>(null);
  const faqSectionRef = useRef<HTMLElement>(null);
  const waitlistSectionRef = useRef<HTMLElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    const formData = new FormData();
    formData.append('email', email);

    try {
      const result = await joinWaitlist(formData);

      if (result.error) {
        setFormError(result.error);
        setIsSubmitting(false);
      } else if (result.success) {
        setIsSuccess(true);
        setIsSubmitting(false);
        // Send GA event for successful waitlist signup
        sendGAEvent({
          // No PII — the waitlist email is never sent to analytics.
          ...GA_EVENTS.WAITLIST.SIGNUP,
        });
      }
    } catch (error) {
      setFormError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleArrowClick = () => {
    // Scroll to the introduction section
    if (introSectionRef.current) {
      introSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleWatchFilm = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsPlayingFullVideo(true);

    // Send GA event for video play
    sendGAEvent({
      ...GA_EVENTS.VIDEO.PLAY,
      label: 'The Hunt Full Length',
    });

    // Only load the video source when the button is clicked
    if (!isVideoLoaded && fullVideoRef.current) {
      setIsVideoLoaded(true);
      // Set the source dynamically to prevent preloading
      const source = document.createElement('source');
      source.src =
        'https://epzdgwlmg2kkdfwr.public.blob.vercel-storage.com/videos/the-hunt-full-length-mobile.mp4';
      source.type = 'video/mp4';
      fullVideoRef.current.appendChild(source);
      fullVideoRef.current.load();
    }

    // Start playing the full video after a short delay to allow for fade transition
    setTimeout(() => {
      if (fullVideoRef.current) {
        // Request fullscreen on mobile devices
        if (isMobile) {
          try {
            if (fullVideoRef.current.requestFullscreen) {
              fullVideoRef.current.requestFullscreen();
            } else if ((fullVideoRef.current as any).webkitRequestFullscreen) {
              (fullVideoRef.current as any).webkitRequestFullscreen();
            } else if ((fullVideoRef.current as any).msRequestFullscreen) {
              (fullVideoRef.current as any).msRequestFullscreen();
            }
          } catch (error) {
            console.error('Error attempting to enable fullscreen:', error);
          }
        }

        // Set playback quality for mobile
        if (isMobile) {
          // Try to use lower quality on mobile
          fullVideoRef.current.setAttribute('preload', 'metadata');
          // Set lower resolution for mobile
          fullVideoRef.current.setAttribute('playsinline', '');
        }

        fullVideoRef.current.play();
      }
    }, 300);
  };

  // Handle video end
  const handleVideoEnd = () => {
    setIsPlayingFullVideo(false);

    // Send GA event for video completion
    sendGAEvent({
      ...GA_EVENTS.VIDEO.COMPLETE,
      label: 'The Hunt Full Length',
    });

    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Error exiting fullscreen:', err);
      });
    }
  };

  // Track section visibility for analytics
  useEffect(() => {
    // Send pageview event when component mounts
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        page_title: 'Landing Page',
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
    }

    // Setup intersection observer to track when sections are viewed
    const observerOptions = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.3, // trigger when 30% of the element is visible
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          // Send section view event
          sendGAEvent({
            ...GA_EVENTS.SECTION.VIEW,
            label: sectionId,
          });
        }
      });
    }, observerOptions);

    // Observe all section refs
    const sections = [
      { ref: heroSectionRef, id: 'hero-section' },
      { ref: howItWorksSectionRef, id: 'how-it-works-section' },
      { ref: audiencesSectionRef, id: 'audiences-section' },
      { ref: teamSectionRef, id: 'team-section' },
      { ref: faqSectionRef, id: 'faq-section' },
      { ref: waitlistSectionRef, id: 'waitlist-section' },
    ];

    sections.forEach((section) => {
      if (section.ref.current) {
        sectionObserver.observe(section.ref.current);
      }
    });

    // Cleanup
    return () => {
      sections.forEach((section) => {
        if (section.ref.current) {
          sectionObserver.unobserve(section.ref.current);
        }
      });
    };
  }, []);

  // Track video pause events
  useEffect(() => {
    if (fullVideoRef.current && isPlayingFullVideo) {
      // Store ref value in a variable to use in cleanup
      const videoElement = fullVideoRef.current;

      const handlePause = () => {
        // Don't send event if video ended (we already track completion separately)
        if (!videoElement.ended) {
          sendGAEvent({
            ...GA_EVENTS.VIDEO.PAUSE,
            label: 'The Hunt Full Length',
            // Add current time as value to track where users are pausing
            value: Math.floor(videoElement.currentTime || 0),
          });
        }
      };

      videoElement.addEventListener('pause', handlePause);

      return () => {
        videoElement.removeEventListener('pause', handlePause);
      };
    }
  }, [isPlayingFullVideo]);

  // Check if the browser is a mobile browser
  useEffect(() => {
    const checkMobile = () => {
      if (typeof window === 'undefined') return false;

      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      const mobileRegex =
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i;
      const mobileCheck =
        mobileRegex.test(userAgent) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          userAgent.substr(0, 4)
        );

      return mobileCheck || window.innerWidth < 768;
    };

    setIsMobile(checkMobile());

    // Update on resize
    const handleResize = () => {
      setIsMobile(checkMobile());
    };

    window.addEventListener('resize', handleResize);

    // Track outbound link clicks
    const trackOutboundLink = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (link && link.href && link.hostname !== window.location.hostname) {
        sendGAEvent({
          ...GA_EVENTS.OUTBOUND_LINK.CLICK,
          label: link.href,
        });
      }
    };

    document.addEventListener('click', trackOutboundLink);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', trackOutboundLink);
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white w-full landing-page">
      <div className="flex flex-col w-full mx-auto bg-sourced-white">
        {/* Hero Section */}
        <section
          ref={heroSectionRef}
          id="hero-section"
          className="relative text-sourced-white"
          style={{ height: '956px' }}
        >
          {/* Static Hero Background Image (always visible first) */}
          <div className="absolute inset-0 z-0 overflow-hidden">
            <Image
              src="/images/landing/a-love-letter-to-sourcing-hero.png"
              alt="Sourced By Hero"
              fill
              priority
              className="object-cover"
            />
            <div className="absolute inset-0 bg-sourced-black bg-opacity-25"></div>
          </div>

          {/* Hero Background Video with Overlay (loads on top of image) */}
          <div
            className={`absolute inset-0 z-10 overflow-hidden transition-opacity duration-500 ${isPlayingFullVideo ? 'opacity-0' : 'opacity-100'}`}
          >
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute w-full h-full object-cover"
              style={{ minWidth: '100%', minHeight: '100%' }}
            >
              <source
                src="https://epzdgwlmg2kkdfwr.public.blob.vercel-storage.com/videos/the-hunt-hero-loop.mp4"
                type="video/mp4"
              />
            </video>
            <div className="absolute inset-0 bg-sourced-black bg-opacity-25"></div>
          </div>

          {/* Full-length video player (hidden initially) */}
          <div
            ref={videoContainerRef}
            className={`absolute inset-0 z-30 overflow-hidden transition-opacity duration-500 flex items-center justify-center ${isPlayingFullVideo ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            style={{ touchAction: 'auto' }}
          >
            <div className="relative w-full h-full bg-black flex items-center justify-center">
              <video
                ref={fullVideoRef}
                playsInline
                controls
                controlsList="nodownload"
                className="max-w-full max-h-full"
                style={{ objectFit: 'contain' }}
                preload="none"
                onEnded={handleVideoEnd}
              >
                {/* Source will be added dynamically when the button is clicked */}
              </video>
              {isPlayingFullVideo && !isVideoLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white">Loading video...</div>
                </div>
              )}
            </div>
          </div>

          {/* Hero Content Container */}
          <div
            className={`relative ${isPlayingFullVideo ? 'z-20' : 'z-20'} h-full w-full flex items-center justify-center`}
          >
            <div className="container mx-auto px-4 h-full flex flex-col items-center justify-center">
              {/* Logo */}
              <div
                className={`flex flex-col items-center justify-center transition-opacity duration-500 ${isPlayingFullVideo ? 'opacity-0' : 'opacity-100'} absolute top-12`}
              >
                <Image
                  src="/images/SourcedBy_logo_white.svg"
                  alt="Sourced By Logo"
                  width={117}
                  height={36}
                  priority
                />
                <Link
                  href={isAuthenticated ? dashboardHref : '/login'}
                  className="flex items-center justify-center text-sourced-white border border-white rounded-full transition duration-300 hover:bg-white hover:bg-opacity-10 mt-2"
                  style={{
                    width: isMobile ? '100px' : '120px',
                    height: isMobile ? '28px' : '32px',
                    borderWidth: '1px',
                    borderRadius: '100px',
                    fontFamily: 'Founders Grotesk Text Regular',
                    fontWeight: 600,
                    fontSize: isMobile ? '12px' : '12px',
                    lineHeight: '90%',
                    letterSpacing: '0%',
                    textAlign: 'center',
                  }}
                  aria-label={isAuthenticated ? 'Go to Dashboard' : 'Login'}
                >
                  {isAuthenticated ? 'DASHBOARD' : 'LOGIN'}
                </Link>
              </div>

              {/* Title Lockup - Conditional rendering for mobile/desktop */}
              {isMobile ? (
                <div
                  className={`flex justify-center transition-opacity duration-500 ${isPlayingFullVideo ? 'opacity-0' : 'opacity-100'}`}
                >
                  <Image
                    src="/images/landing/hero-title-lockup-mobile.svg"
                    alt="A Love Letter to Sourcing"
                    width={350}
                    height={239.18}
                    priority
                  />
                </div>
              ) : (
                <div
                  className={`flex justify-center transition-opacity duration-500 ${isPlayingFullVideo ? 'opacity-0' : 'opacity-100'}`}
                >
                  <Image
                    src="/images/landing/hero-title-lockup.svg"
                    alt="A Love Letter to Sourcing"
                    width={824.55}
                    height={350}
                    priority
                  />
                </div>
              )}

              {/* Button Container - Positioned conditionally based on device */}
              {!isPlayingFullVideo && (
                <>
                  {isMobile ? (
                    <div
                      className="flex flex-col items-center justify-center mx-auto transition-opacity duration-500"
                      style={{ marginTop: '40px' }}
                    >
                      <button
                        onClick={handleWatchFilm}
                        className="flex items-center justify-center text-sourced-white border border-white rounded-full transition duration-300 hover:bg-white hover:bg-opacity-10"
                        style={{
                          width: '200px',
                          height: '46px',
                          borderWidth: '1px',
                          borderRadius: '100px',
                          gap: '4px',
                          paddingTop: '16px',
                          paddingRight: '24px',
                          paddingBottom: '16px',
                          paddingLeft: '24px',
                          fontFamily: 'Founders Grotesk Text Regular',
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                          textAlign: 'center',
                        }}
                      >
                        WATCH THE FILM
                      </button>
                      <div
                        className="mt-[97px] animate-pulse-down cursor-pointer"
                        onClick={handleArrowClick}
                        role="button"
                        tabIndex={0}
                        aria-label="Scroll to introduction"
                        onKeyDown={(e) =>
                          e.key === 'Enter' && handleArrowClick()
                        }
                      >
                        <Image
                          src="/images/landing/icon-down.svg"
                          alt="Scroll down"
                          width={48}
                          height={48}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="absolute left-0 right-0 bottom-[48px] transition-opacity duration-500">
                      <div className="flex justify-center mx-auto">
                        <button
                          onClick={handleWatchFilm}
                          className="flex items-center justify-center text-sourced-white border border-white rounded-full transition duration-300 hover:bg-white hover:bg-opacity-10"
                          style={{
                            width: '231px',
                            height: '64px',
                            borderWidth: '1px',
                            gap: '48px',
                            fontFamily: 'Founders Grotesk Text Regular',
                            fontWeight: 600,
                            fontSize: '20px',
                            lineHeight: '90%',
                            letterSpacing: '0%',
                            textAlign: 'center',
                          }}
                        >
                          WATCH THE FILM
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="w-full overflow-hidden">
          <div
            ref={introSectionRef}
            className="w-full mx-auto px-16"
            style={{
              paddingTop: '48px',
            }}
          >
            <div className="max-w-full mx-auto pb-[64px] border-b border-sourced-black">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-start w-full lg:space-x-8">
                {/* Text Block */}
                <div className="w-full lg:w-auto">
                  <div className="text-left mb-8 lg:mb-0">
                    <p
                      className="text-sourced-black"
                      style={{
                        width: '100%',
                        fontSize: '24px',
                        lineHeight: '90%',
                        letterSpacing: '-3%',
                      }}
                    >
                      Sourced By&reg; is an AI-automated, all-in-one platform
                      that streamlines every aspect of the fashion sourcing
                      process. We are designed to ensure every request ends in a
                      #sourcedby moment.
                    </p>
                  </div>
                </div>

                {/* Form Component */}
                <div className="flex items-start lg:items-end justify-start lg:justify-end w-full lg:flex-1 lg:mb-0">
                  {isSuccess ? (
                    <div
                      className="flex items-center w-full md:w-auto"
                      style={{
                        height: '44px',
                        padding: '16px',
                        background: '#0000000D',
                        minWidth: '332px',
                      }}
                    >
                      <div className="flex items-center justify-center">
                        <Image
                          src="/images/landing/icon-check.svg"
                          alt="Success"
                          width={24}
                          height={24}
                        />
                      </div>
                      <div
                        className="text-sourced-black text-right flex items-center justify-end"
                        style={{
                          marginLeft: '20px',
                          fontWeight: 600,
                          fontSize: '20px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        SUCCESS
                      </div>
                    </div>
                  ) : (
                    <form
                      ref={formRef}
                      onSubmit={handleSubmit}
                      className="w-full lg:w-auto lg:ml-auto"
                    >
                      <div className="flex flex-col md:flex-row items-stretch space-y-4 md:space-y-0 md:space-x-6 w-full">
                        <input
                          type="email"
                          name="email"
                          placeholder="EMAIL"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="text-left text-sourced-black focus:outline-none border border-sourced-black placeholder:text-sourced-black placeholder:opacity-50 w-full md:w-[298px]"
                          style={{
                            height: '44px',
                            borderWidth: '1px',
                            padding: '12px 16px',
                            fontSize: '16px',
                            lineHeight: '90%',
                            letterSpacing: '0%',
                          }}
                          disabled={isSubmitting}
                          required
                        />
                        <button
                          type="submit"
                          className={`bg-sourced-black rounded-full whitespace-nowrap w-full md:w-[206px]`}
                          style={{
                            height: '44px',
                            fontSize: '16px',
                            lineHeight: '90%',
                            letterSpacing: '0%',
                            textAlign: 'center',
                            color:
                              email && !isSubmitting ? '#FFFFFF' : '#FFFFFF80',
                          }}
                          disabled={!email || isSubmitting}
                        >
                          {isSubmitting ? 'SUBMITTING...' : 'APPLY FOR ACCESS'}
                        </button>
                      </div>
                      {formError && (
                        <p className="mt-2 text-red-600 text-sm">{formError}</p>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="w-full flex justify-center overflow-hidden">
          <div
            className="w-full relative flex flex-col px-16 md:px-[64px] py-16 md:py-[64px]"
            style={{
              gap: '64px',
            }}
          >
            {/* About */}
            <div className="w-full max-w-full mx-auto flex flex-col items-center gap-8">
              {/* Header with black boxes */}
              <div
                className="w-full flex justify-between items-center"
                style={{ height: '12px' }}
              >
                <div className="w-[12px] h-[12px] bg-sourced-black"></div>
                <div className="w-[12px] h-[12px] bg-sourced-black"></div>
              </div>

              {/* Main heading */}
              <div
                className="w-full max-w-full mx-auto text-center text-sourced-black text-3xl md:text-4xl lg:text-5xl"
                style={{
                  lineHeight: '90%',
                  letterSpacing: '-0.04em',
                }}
              >
                Sourced By is built for Sourcers, Shoppers, Brands & Retailers
              </div>

              {/* Subheading */}
              <div
                className="w-full max-w-full mx-auto text-center text-sourced-black text-xl md:text-2xl"
                style={{
                  lineHeight: '90%',
                  letterSpacing: '-0.03em',
                }}
              >
                A tool tailored for fashion professionals all in one beautifully
                efficient system
              </div>

              {/* Footer with black boxes */}
              <div
                className="w-full flex justify-between items-center"
                style={{ height: '12px' }}
              >
                <div className="w-[12px] h-[12px] bg-sourced-black"></div>
                <div className="w-[12px] h-[12px] bg-sourced-black"></div>
              </div>
            </div>

            {/* Audiences */}
            <div className="w-full max-w-full mx-auto flex flex-col gap-8">
              {/* Audience Cards Container */}
              <div className="w-full pb-[64px] border-b border-sourced-black">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Sourcers Card */}
                  <div className="flex flex-col gap-4">
                    <div className="relative w-full aspect-[433/320] md:aspect-auto md:h-[320px]">
                      <Image
                        src="/images/landing/image-sourcers.jpg"
                        alt="Sourcers"
                        width={433}
                        height={320}
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <div>
                      <h3
                        className="text-sourced-black mb-[20px]"
                        style={{
                          fontSize: '32px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        SOURCERS
                      </h3>
                      <p
                        className="text-sourced-black"
                        style={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          letterSpacing: '0%',
                        }}
                      >
                        The client-facing leaders of the process, who manage
                        requests and relationships.
                      </p>
                    </div>
                  </div>

                  {/* Shoppers Card */}
                  <div className="flex flex-col gap-4">
                    <div className="relative w-full aspect-[433/320] md:aspect-auto md:h-[320px]">
                      <Image
                        src="/images/landing/image-shoppers.jpg"
                        alt="Shoppers"
                        width={433}
                        height={320}
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <div>
                      <h3
                        className="text-sourced-black mb-[20px]"
                        style={{
                          fontSize: '32px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        SHOPPERS
                      </h3>
                      <p
                        className="text-sourced-black"
                        style={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          letterSpacing: '0%',
                        }}
                      >
                        Global detectives working behind the scenes to source
                        and secure pieces.
                      </p>
                    </div>
                  </div>

                  {/* Brands & Retailers Card */}
                  <div className="flex flex-col gap-4">
                    <div className="relative w-full aspect-[433/320] md:aspect-auto md:h-[320px]">
                      <Image
                        src="/images/landing/image-brands.jpg"
                        alt="Brands & Retailers"
                        width={433}
                        height={320}
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <div>
                      <h3
                        className="text-sourced-black mb-[20px]"
                        style={{
                          fontSize: '32px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        BRANDS & RETAILERS
                      </h3>
                      <p
                        className="text-sourced-black"
                        style={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          letterSpacing: '0%',
                        }}
                      >
                        Power users who act as Shoppers for specific categories
                        or geographies.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section
          className="w-full bg-sourced-white px-16"
          style={{
            gap: '4px',
          }}
        >
          <div
            className="w-full mx-auto"
            style={{
              gap: '32px',
              paddingBottom: isMobile ? '24px' : '64px',
              overflow: 'hidden',
            }}
          >
            <div
              className="w-full flex flex-col items-center"
              style={{
                gap: '48px',
                paddingBottom: '24px',
              }}
            >
              {/* Title */}
              <h2
                className="text-sourced-black text-center uppercase font-sans"
                style={{
                  fontWeight: 400,
                  fontSize: '32px',
                  lineHeight: '90%',
                  letterSpacing: '0%',
                }}
              >
                How It Works
              </h2>

              {/* Steps Container */}
              <div
                className="w-full"
                style={{
                  gap: '32px',
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-[32px] w-full overflow-x-auto pb-4 items-start">
                  {/* Step 1 */}
                  <div
                    className="flex flex-col w-full md:border-b-0 border-b border-sourced-black pb-4 md:pb-0"
                    style={{
                      gap: '8px',
                    }}
                  >
                    <div
                      className="flex flex-col w-full"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        1
                      </p>
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        UPLOAD CLIENT REQUEST
                      </p>
                    </div>
                    <div
                      className="w-full mt-2"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black"
                        style={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          letterSpacing: '0%',
                        }}
                      >
                        Screenshot and upload your client&apos;s request.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div
                    className="flex flex-col w-full md:border-b-0 border-b border-sourced-black pb-4 md:pb-0"
                    style={{
                      gap: '8px',
                    }}
                  >
                    <div
                      className="flex flex-col w-full"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        2
                      </p>
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        AI DOES THE WORK
                      </p>
                    </div>
                    <div
                      className="w-full mt-2"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black"
                        style={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          letterSpacing: '0%',
                        }}
                      >
                        Our AI extracts and standardizes the item&apos;s details
                        into a simple request.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div
                    className="flex flex-col w-full md:border-b-0 border-b border-sourced-black pb-4 md:pb-0"
                    style={{
                      gap: '8px',
                    }}
                  >
                    <div
                      className="flex flex-col w-full"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        3
                      </p>
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        THE HUNT BEGINS
                      </p>
                    </div>
                    <div
                      className="w-full mt-2"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black"
                        style={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          letterSpacing: '0%',
                        }}
                      >
                        Shoppers on your team can review the request and start
                        their search.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div
                    className="flex flex-col w-full md:border-b-0 border-b border-sourced-black pb-4 md:pb-0"
                    style={{
                      gap: '8px',
                    }}
                  >
                    <div
                      className="flex flex-col w-full"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        4
                      </p>
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        TEAM GETS PAID
                      </p>
                    </div>
                    <div
                      className="w-full mt-2"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black"
                        style={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          letterSpacing: '0%',
                        }}
                      >
                        Once found, generate quotes and invoices with just one
                        click.
                      </p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div
                    className="flex flex-col w-full md:border-b-0 border-b border-sourced-black pb-4 md:pb-0"
                    style={{
                      gap: '8px',
                    }}
                  >
                    <div
                      className="flex flex-col w-full"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        5
                      </p>
                      <p
                        className="text-sourced-black uppercase"
                        style={{
                          fontSize: '16px',
                          lineHeight: '90%',
                          letterSpacing: '0%',
                        }}
                      >
                        TRACK IT ALL
                      </p>
                    </div>
                    <div
                      className="w-full mt-2"
                      style={{
                        gap: '4px',
                      }}
                    >
                      <p
                        className="text-sourced-black"
                        style={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          letterSpacing: '0%',
                        }}
                      >
                        CRM and shipments all in one place.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Forbes Quote Section */}
        <section
          className="w-full relative overflow-hidden"
          style={{
            height: '450px',
            gap: '4px',
          }}
        >
          {/* Background Image */}
          <div className="absolute inset-0 w-full h-full">
            <Image
              src="/images/landing/quote-forbes-background.png"
              alt="Forbes Quote Background"
              fill
              className="object-cover"
              priority
            />
          </div>

          {/* Content Container */}
          <div
            className="w-full h-full mx-auto relative z-10 flex flex-col items-center justify-center px-4 md:px-[64px]"
            style={{
              gap: '32px',
            }}
          >
            <div
              className="w-full flex flex-col items-center"
              style={{
                gap: '20px',
              }}
            >
              {/* Quote Text */}
              <p
                className="text-white text-center text-2xl md:text-3xl lg:text-5xl"
                style={{
                  lineHeight: '90%',
                  letterSpacing: '-0.04em',
                }}
              >
                &ldquo;By combining cutting-edge technology with human
                expertise, we&rsquo;re bridging the gap between desire and
                discovery, making the impossible-to-find not just possible, but
                effortless.&rdquo;
              </p>

              {/* Attribution */}
              <p
                className="text-white text-center uppercase text-sm md:text-base"
                style={{
                  lineHeight: '90%',
                  letterSpacing: '0%',
                }}
              >
                <a
                  href="http://forbes.com/sites/karineldor/2024/12/19/gab-waller-is-revolutionizing-fashion-with-ai-powered-platform-sourced-by-with-stockx-exec-as-cto/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:underline"
                >
                  scott davis, forbes
                </a>
              </p>
            </div>
          </div>
          <div className="w-full flex justify-between items-center mt-[48px]">
            <div className="w-[12px] h-[12px] bg-sourced-black"></div>
            <div className="w-[12px] h-[12px] bg-sourced-black"></div>
          </div>
        </section>

        {/* Love Notes (Testimonials) Section */}
        <section
          className="w-full bg-sourced-white px-4 md:px-[64px]"
          style={{
            gap: '4px',
          }}
        >
          <div
            className="w-full mx-auto"
            style={{
              gap: '48px',
              paddingTop: '64px',
              paddingBottom: '64px',
            }}
          >
            {/* Header with squares */}
            <div className="w-full flex justify-between items-center">
              <div className="w-[12px] h-[12px] bg-sourced-black"></div>
              <div className="w-[12px] h-[12px] bg-sourced-black"></div>
            </div>

            {/* Title */}
            <h2
              className="text-sourced-black text-center uppercase mt-[48px] font-sans"
              style={{
                fontWeight: 400,
                fontSize: '32px',
                lineHeight: '90%',
                letterSpacing: '0%',
              }}
            >
              Love Notes
            </h2>

            {/* Quote Container */}
            <div
              className="w-full mx-auto mt-[48px] px-4 md:px-[64px]"
              style={{
                gap: '32px',
              }}
            >
              {/* Quotes Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-x-16 md:gap-y-12">
                {/* Quote 1 */}
                <div
                  className="flex flex-col rounded-sm"
                  style={{
                    gap: '20px',
                  }}
                >
                  <p
                    className="text-sourced-black uppercase font-sans"
                    style={{
                      fontSize: '16px',
                      lineHeight: '90%',
                      letterSpacing: '0%',
                    }}
                  >
                    jean—Shopper, France
                  </p>
                  <p
                    className="text-sourced-black font-sans"
                    style={{
                      fontWeight: 400,
                      fontSize: '24px',
                      lineHeight: '90%',
                      letterSpacing: '-0.03em',
                    }}
                  >
                    &ldquo;Love how Sourced By is shopper friendly. It makes it
                    so much easier and fast to track and find some hidden gems
                    for clients.&rdquo;
                  </p>
                </div>

                {/* Quote 2 */}
                <div
                  className="flex flex-col rounded-sm"
                  style={{
                    gap: '20px',
                  }}
                >
                  <p
                    className="text-sourced-black uppercase font-sans"
                    style={{
                      fontSize: '16px',
                      lineHeight: '90%',
                      letterSpacing: '0%',
                    }}
                  >
                    annie—Shopper, Canada
                  </p>
                  <p
                    className="text-sourced-black font-sans"
                    style={{
                      fontWeight: 400,
                      fontSize: '24px',
                      lineHeight: '90%',
                      letterSpacing: '-0.03em',
                    }}
                  >
                    &ldquo;Being able to track every client&rsquo;s request from
                    start to finish on a single platform that does it all has
                    been a game changer for me. I would never go back!&rdquo;
                  </p>
                </div>

                {/* Quote 3 */}
                <div
                  className="flex flex-col rounded-sm"
                  style={{
                    gap: '20px',
                  }}
                >
                  <p
                    className="text-sourced-black uppercase font-sans"
                    style={{
                      fontSize: '16px',
                      lineHeight: '90%',
                      letterSpacing: '0%',
                    }}
                  >
                    noam—Shopper, Australia
                  </p>
                  <p
                    className="text-sourced-black font-sans"
                    style={{
                      fontWeight: 400,
                      fontSize: '24px',
                      lineHeight: '90%',
                      letterSpacing: '-0.03em',
                    }}
                  >
                    &ldquo;It&rsquo;s empowering to finally have a dedicated
                    platform that streamlines the process and lets us focus on
                    what matters most—connecting our clients with their dream
                    pieces.&rdquo;
                  </p>
                </div>

                {/* Quote 4 */}
                <div
                  className="flex flex-col rounded-sm"
                  style={{
                    gap: '20px',
                  }}
                >
                  <p
                    className="text-sourced-black uppercase font-sans"
                    style={{
                      fontSize: '16px',
                      lineHeight: '90%',
                      letterSpacing: '0%',
                    }}
                  >
                    elaine—Shopper, United Kingdom
                  </p>
                  <p
                    className="text-sourced-black font-sans"
                    style={{
                      fontWeight: 400,
                      fontSize: '24px',
                      lineHeight: '90%',
                      letterSpacing: '-0.03em',
                    }}
                  >
                    &ldquo;This is exactly why Sourced By is so fab, I would
                    never have located this 2023 request in Asana—the original
                    description would have thrown me off...&rdquo;
                  </p>
                </div>
              </div>
            </div>

            {/* Footer with squares */}
            <div className="w-full flex justify-between items-center mt-[48px]">
              <div className="w-[12px] h-[12px] bg-sourced-black"></div>
              <div className="w-[12px] h-[12px] bg-sourced-black"></div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section
          className="w-full bg-[#FFCCD9] px-4 md:px-[64px]"
          style={{
            gap: '48px',
            paddingTop: '96px',
            paddingBottom: '96px',
          }}
        >
          <h2
            className="text-sourced-black text-center uppercase"
            style={{
              fontWeight: 400,
              fontSize: '24px',
              lineHeight: '90%',
              letterSpacing: '0%',
            }}
          >
            The Team
          </h2>

          <div
            className="w-full mx-auto"
            style={{
              gap: '6px',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
              {/* Team Member 1 - Gab */}
              <div className="mt-[48px] flex flex-col items-center">
                <div
                  className="relative w-full overflow-hidden"
                  style={{ aspectRatio: '433.33/495' }}
                >
                  <Image
                    src="/images/landing/gab.jpg"
                    alt="Gab Waller"
                    fill
                    className="object-cover object-top"
                  />
                </div>
                <div className="flex flex-col mt-[48px]">
                  <div>
                    <p
                      className="text-sourced-black uppercase"
                      style={{
                        fontSize: '32px',
                        lineHeight: '90%',
                        letterSpacing: '0%',
                        textTransform: 'uppercase',
                      }}
                    >
                      GAB WALLER
                    </p>
                    <p
                      className="text-sourced-black uppercase"
                      style={{
                        fontSize: '16px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        textTransform: 'uppercase',
                      }}
                    >
                      FOUNDER AND CEO
                    </p>
                  </div>
                  <p
                    className="text-sourced-black mt-4 pr-3"
                    style={{
                      fontSize: '16px',
                      lineHeight: '120%',
                      letterSpacing: '0%',
                    }}
                  >
                    Gab has pioneered the fashion sourcing industry and has over
                    7 years experience as the top-performing and leading expert
                    in fashion sourcing. Her sourcing work has been recognized
                    by and featured in Forbes, WSJ, NYT, Vogue, WWD, and Vanity
                    Fair. She maintains a growing roster of global clients
                    including Hailey Bieber, Kris Jenner, Khloé Kardashian,
                    Sofia Richie Grainge, Rosie Huntington-Whiteley, and Lori
                    Harvey.
                  </p>
                </div>
              </div>

              {/* Team Member 2 - Alisha */}
              <div className="mt-[48px] flex flex-col items-center">
                <div
                  className="relative w-full overflow-hidden"
                  style={{ aspectRatio: '433.33/495' }}
                >
                  <Image
                    src="/images/landing/alisha.jpg"
                    alt="Alisha Kumar"
                    fill
                    className="object-cover object-top"
                  />
                </div>
                <div className="mt-[48px] flex flex-col">
                  <div>
                    <p
                      className="text-sourced-black uppercase"
                      style={{
                        fontSize: '32px',
                        lineHeight: '90%',
                        letterSpacing: '0%',
                        textTransform: 'uppercase',
                      }}
                    >
                      ALISHA KUMAR
                    </p>
                    <p
                      className="text-sourced-black uppercase"
                      style={{
                        fontSize: '16px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        textTransform: 'uppercase',
                      }}
                    >
                      COO
                    </p>
                  </div>
                  <p
                    className="text-sourced-black mt-4 pr-3"
                    style={{
                      fontSize: '16px',
                      lineHeight: '120%',
                      letterSpacing: '0%',
                    }}
                  >
                    Alisha is an operator with a deep love for the early stage.
                    An NYU graduate and Stanford researcher, she helped drive
                    her previous company through to acquisition as the first
                    business hire. She brings to the team a mission-driven lens
                    to building teams and products that help humans connect and
                    build meaningfully.
                  </p>
                </div>
              </div>

              {/* Team Member 3 - Scott */}
              <div className="mt-[48px] flex flex-col items-center">
                <div
                  className="relative w-full overflow-hidden"
                  style={{ aspectRatio: '433.33/495' }}
                >
                  <Image
                    src="/images/landing/scott.jpg"
                    alt="Scott Davis"
                    fill
                    className="object-cover object-top"
                  />
                </div>
                <div className="mt-[48px] flex flex-col">
                  <div>
                    <p
                      className="text-sourced-black uppercase"
                      style={{
                        fontSize: '32px',
                        lineHeight: '90%',
                        letterSpacing: '0%',
                        textTransform: 'uppercase',
                      }}
                    >
                      SCOTT DAVIS
                    </p>
                    <p
                      className="text-sourced-black uppercase"
                      style={{
                        fontSize: '16px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        textTransform: 'uppercase',
                      }}
                    >
                      Advisor
                    </p>
                  </div>
                  <p
                    className="text-sourced-black mt-4 pr-3"
                    style={{
                      fontSize: '16px',
                      lineHeight: '120%',
                      letterSpacing: '0%',
                    }}
                  >
                    Scott brings 25+ years of hands-on engineering experience,
                    two successful founder exits, and deep marketplace
                    expertise. He was a founding engineer at StockX and later
                    returned as VP of Engineering, where he led platform growth
                    and pushed the boundaries of AI innovation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-white w-full mx-auto px-6 md:px-[64px] py-16 md:py-24">
          <h2
            className="text-sourced-black text-center uppercase"
            style={{
              fontWeight: 400,
              fontSize: '24px',
              lineHeight: '90%',
              letterSpacing: '0%',
            }}
          >
            Frequently Asked Questions
          </h2>
          <div
            className="w-full mx-auto mt-[50px]"
            style={{
              gap: '32px',
            }}
          >
            {/* FAQ Item 1 */}
            <FaqItem
              question="I would like to become a freelance Shopper, what is the process for this?"
              answer="We welcome applications from freelance Shoppers across all locations and experience levels. To get started, apply for access. Selected applicants will be invited to complete a short evaluation and, upon approval, will receive one-on-one onboarding to get started on Sourced By."
            />

            {/* FAQ Item 2 */}
            <FaqItem
              question="I would like to submit a sourcing request, how can I do this?"
              answer={
                <>
                  You can email{' '}
                  <a href="mailto:source@sourcedby.com" className="underline">
                    source@sourcedby.com
                  </a>{' '}
                  with your sourcing request. We kindly ask that your request
                  includes an image of the style that you&rsquo;re looking for,
                  your preferred size, and where you&rsquo;re based. Your
                  request will be forwarded onto the Sourcers that are active on
                  our platform.
                </>
              }
            />

            {/* FAQ Item 3 */}
            <FaqItem
              question="Will my clients use the platform?"
              answer="No. Clients don't interact with Sourced By. The platform is built entirely for your internal workflow — preserving your relationships, your brand, and your way of working."
            />

            {/* FAQ Item 4 */}
            <FaqItem
              question="Can I customize my invoices?"
              answer="Yes. Sourced By allows you to upload your logo, business info, and terms and conditions. Invoices are generated through your linked Stripe account, sent directly to clients, and reflect your business."
            />

            {/* FAQ Item 5 */}
            <FaqItem
              question="What makes Sourced By different from other tools?"
              answer="Every part of Sourced By was designed from the ground up for the unique, detail-heavy world of sourcing. From automated item recognition to smart quoting, our platform is intuitive and made for scale."
            />

            {/* FAQ Item 6 */}
            <FaqItem
              question="Is Sourced By right for me if I'm just getting started?"
              answer="Absolutely. We've designed our system to work for both seasoned Sourcers and those building their client base. By taking care of the backend from day one, we help you stay focused on what builds reputation: reliability, responsiveness, and remarkable finds."
            />

            {/* FAQ Item 7 */}
            <FaqItem
              question="What countries and currencies do you accept?"
              answer="We are global, and can provide flexibility on the currencies that you invoice in."
            />

            {/* FAQ Item 8 */}
            <FaqItem
              question="How do you guarantee my client's information is protected?"
              answer="We use 256-bit AES encryption with a securely generated 32-byte key to protect client PII. Encryption keys are stored exclusively in environment variables, never in source code, and we support secure key rotation protocols to maintain confidentiality, integrity, and compliance across all environments."
            />

            {/* FAQ Item 9 */}
            <FaqItem
              question="How do I sign up?"
              answer="Sourced By is now live and accepting new users. To get started, simply apply for access using the form above. Once approved, you'll receive onboarding instructions and can begin using the platform."
            />
          </div>
        </section>

        {/* Press Section */}
        <section
          className="bg-sourced-black w-full mx-auto px-6 md:px-[64px] py-8"
          style={{
            gap: '32px',
          }}
        >
          <div
            className="w-full mx-auto"
            style={{
              gap: '32px',
              paddingBottom: '96px',
              borderBottomWidth: '1px',
              borderBottomColor: 'white',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Article 1 (New) */}
              <div className="flex flex-col gap-5">
                <p
                  className="text-white uppercase"
                  style={{
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '90%',
                    letterSpacing: '0',
                    textTransform: 'uppercase',
                  }}
                >
                  vogue
                </p>
                <h3
                  className="text-white"
                  style={{
                    fontWeight: 400,
                    fontSize: '32px',
                    lineHeight: '90%',
                    letterSpacing: '-0.03em',
                  }}
                >
                  Introducing the Vogue Business 100 Innovators: Class of 2025
                </h3>
                <div className="flex w-full mt-5">
                  <a
                    href="https://www.voguebusiness.com/story/fashion/introducing-the-vogue-business-100-innovators-class-of-2025"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white underline"
                    style={{
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '100%',
                      letterSpacing: '0',
                      textAlign: 'left',
                      textDecorationLine: 'underline',
                      textDecorationStyle: 'solid',
                      textUnderlineOffset: '4px',
                      textDecorationThickness: '1px',
                    }}
                  >
                    READ MORE
                  </a>
                </div>
              </div>

              {/* Article 2 (was Article 1) */}
              <div className="flex flex-col gap-5">
                <p
                  className="text-white uppercase"
                  style={{
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '90%',
                    letterSpacing: '0',
                    textTransform: 'uppercase',
                  }}
                >
                  vogue
                </p>
                <h3
                  className="text-white"
                  style={{
                    fontWeight: 400,
                    fontSize: '32px',
                    lineHeight: '90%',
                    letterSpacing: '-0.03em',
                  }}
                >
                  Luxury fashion&apos;s power sourcer is reinventing the job
                  with AI
                </h3>
                <div className="flex w-full mt-5">
                  <a
                    href="https://www.voguebusiness.com/story/technology/luxury-fashions-power-sourcer-is-reinventing-the-job-with-ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white underline"
                    style={{
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '100%',
                      letterSpacing: '0',
                      textAlign: 'left',
                      textDecorationLine: 'underline',
                      textDecorationStyle: 'solid',
                      textUnderlineOffset: '4px',
                      textDecorationThickness: '1px',
                    }}
                  >
                    READ MORE
                  </a>
                </div>
              </div>

              {/* Article 3 (was Article 2) */}
              <div className="flex flex-col gap-5">
                <p
                  className="text-white uppercase"
                  style={{
                    paddingTop: '20px',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '90%',
                    letterSpacing: '0',
                    textTransform: 'uppercase',
                  }}
                >
                  forbes
                </p>
                <h3
                  className="text-white"
                  style={{
                    fontWeight: 400,
                    fontSize: '32px',
                    lineHeight: '90%',
                    letterSpacing: '-0.03em',
                  }}
                >
                  Gab Waller Is Revolutionizing Fashion With AI-Powered Platform
                  Sourced By (With StockX Leader As CTO)
                </h3>
                <div className="flex w-full mt-5">
                  <a
                    href="http://forbes.com/sites/karineldor/2024/12/19/gab-waller-is-revolutionizing-fashion-with-ai-powered-platform-sourced-by-with-stockx-exec-as-cto/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white underline"
                    style={{
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '100%',
                      letterSpacing: '0',
                      textAlign: 'left',
                      textDecorationLine: 'underline',
                      textDecorationStyle: 'solid',
                      textUnderlineOffset: '4px',
                      textDecorationThickness: '1px',
                    }}
                  >
                    READ MORE
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Section */}
        <section
          className="bg-sourced-black w-full mx-auto px-6 md:px-[64px] py-6 md:py-8"
          style={{
            gap: '4px',
          }}
        >
          <div
            className="mx-auto flex flex-col items-center w-full"
            style={{
              gap: '68px',
            }}
          >
            {/* Links Section */}
            <div
              className="flex flex-wrap justify-center"
              style={{
                gap: '20px',
              }}
            >
              <Link
                href="/privacy-policy"
                className="text-white uppercase"
                style={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  textAlign: 'right',
                  textDecoration: 'underline',
                  textDecorationStyle: 'solid',
                  textDecorationThickness: '0.07em',
                  textUnderlineOffset: '0.25em',
                }}
              >
                Privacy Policy
              </Link>
              <a
                href={TERMS_OF_SERVICE_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white uppercase"
                style={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  textAlign: 'right',
                  textDecoration: 'underline',
                  textDecorationStyle: 'solid',
                  textDecorationThickness: '0.07em',
                  textUnderlineOffset: '0.25em',
                }}
              >
                Terms of Service
              </a>
              <a
                href="mailto:contact@sourcedby.com"
                className="text-white uppercase"
                style={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  textAlign: 'right',
                  textDecoration: 'underline',
                  textDecorationStyle: 'solid',
                  textDecorationThickness: '0.07em',
                  textUnderlineOffset: '0.25em',
                }}
              >
                Contact Us
              </a>
              <a
                href="https://www.instagram.com/sourcedby/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white uppercase"
                style={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  textAlign: 'right',
                  textDecoration: 'underline',
                  textDecorationStyle: 'solid',
                  textDecorationThickness: '0.07em',
                  textUnderlineOffset: '0.25em',
                }}
              >
                Instagram
              </a>
              <a
                href="https://www.linkedin.com/company/sourced-by/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white uppercase"
                style={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  textAlign: 'right',
                  textDecoration: 'underline',
                  textDecorationStyle: 'solid',
                  textDecorationThickness: '0.07em',
                  textUnderlineOffset: '0.25em',
                }}
              >
                LinkedIn
              </a>
            </div>

            {/* Logo and Tagline Section */}
            <div className="flex flex-col items-center justify-center">
              <div
                className="flex justify-center"
                style={{
                  width: '350px',
                  fontFamily: 'Founders Grotesk Text Regular',
                  fontWeight: 600,
                  fontSize: '24px',
                  lineHeight: '90%',
                  letterSpacing: '-3%',
                  color: 'white',
                }}
              >
                Sourced By&reg;... Find More
              </div>
              <div
                className="flex justify-center"
                style={{ marginTop: '24px' }}
              >
                <Image
                  src="/images/landing/icon-logo.svg"
                  alt="Sourced By Icon"
                  width={12}
                  height={12}
                  className="object-contain"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LandingPage;
